import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Mail, ArrowRight } from 'lucide-react';

export function ForgotPasswordModal({ isOpen, onClose }) {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setEmail('');
            setSent(false);
            setError(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: (import.meta.env.VITE_SITE_URL || 'https://timelinemkt.com.br') + '/reset-password',
            });

            if (error) throw error;
            setSent(true);
        } catch (err) {
            console.error('Error sending reset email:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content forgot-password-modal">
                <button className="close-btn-icon" onClick={onClose}><X size={20} /></button>

                {!sent ? (
                    <>
                        <h2>Recuperar Senha</h2>
                        <p className="modal-desc">Digite seu email para receber um link de redefinição de senha.</p>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Email</label>
                                <div className="input-with-icon">
                                    <Mail size={18} className="input-icon" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="seu@email.com"
                                        required
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button type="submit" className="submit-btn" disabled={loading}>
                                {loading ? 'Enviando...' : 'Enviar Link'} <ArrowRight size={18} />
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="success-state">
                        <div className="success-icon">
                            <Mail size={32} />
                        </div>
                        <h2>Verifique seu email</h2>
                        <p>Enviamos um link de recuperação para <strong>{email}</strong>.</p>
                        <p className="sub-text">Verifique também sua caixa de spam.</p>
                        <button className="close-modal-btn" onClick={onClose}>Voltar para o Login</button>
                    </div>
                )}
            </div>
            <style>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }
                
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }
                
                .forgot-password-modal {
                    max-width: 400px;
                    width: 90%;
                    padding: 30px;
                    text-align: center;
                    position: relative;
                }
                
                .forgot-password-modal h2 {
                    color: #1e293b;
                    font-size: 24px;
                    font-weight: 700;
                    margin-bottom: 10px;
                }
                
                .forgot-password-modal p {
                    color: #475569;
                }
                
                .close-btn-icon {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #9ca3af;
                    padding: 5px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .close-btn-icon:hover {
                    background: #f3f4f6;
                    color: #374151;
                }
                
                .modal-desc {
                    color: #6b7280;
                    margin-bottom: 25px;
                    font-size: 0.95rem;
                }
                
                .input-with-icon {
                    position: relative;
                }
                
                .input-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                }
                
                .input-with-icon input {
                    padding-left: 40px !important;
                }
                
                .submit-btn {
                    width: 100%;
                    padding: 12px;
                    background: #ea580c;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s;
                    margin-top: 10px;
                }
                
                .submit-btn:hover:not(:disabled) {
                    background: #c2410c;
                }
                
                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .success-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 10px 0;
                }
                
                .success-icon {
                    width: 60px;
                    height: 60px;
                    background: #dcfce7;
                    color: #16a34a;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 20px;
                }
                
                .sub-text {
                    font-size: 0.9rem;
                    color: #9ca3af;
                    margin-top: 5px;
                }
                
                .close-modal-btn {
                    margin-top: 25px;
                    background: white;
                    border: 1px solid #d1d5db;
                    padding: 10px 20px;
                    border-radius: 8px;
                    color: #374151;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                
                .close-modal-btn:hover {
                    background: #f9fafb;
                    border-color: #9ca3af;
                }
            `}</style>
        </div>
    );
}
